'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import emptyFunction from 'react-utils/emptyFunction';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { OLAF } from 'HubStyleTokens/colors';
import { BUTTON_RADIUS } from 'HubStyleTokens/sizes';
import I18n from 'I18n';
import localeComparator from 'I18n/utils/localeComparator';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import * as AssetBusinessType from 'ecosystem-marketplace-core/constants/groupTags/AssetBusinessType';
import artEntertainmentImageUrl from 'bender-url!marketplace-assets-base/img/business-types/art-entertainment.png';
import customerServiceImageUrl from 'bender-url!marketplace-assets-base/img/business-types/customer-service.png';
import ecommerceImageUrl from 'bender-url!marketplace-assets-base/img/business-types/ecommerce.png';
import eventManagementImageUrl from 'bender-url!marketplace-assets-base/img/business-types/event-management.png';
import financialServicesImageUrl from 'bender-url!marketplace-assets-base/img/business-types/financial-services.png';
import foodBeverageImageUrl from 'bender-url!marketplace-assets-base/img/business-types/food-beverage.png';
import healthBeautyImageUrl from 'bender-url!marketplace-assets-base/img/business-types/health-beauty.png';
import hrRecruitingImageUrl from 'bender-url!marketplace-assets-base/img/business-types/hr-recruiting.png';
import marketingImageUrl from 'bender-url!marketplace-assets-base/img/business-types/marketing.png';
import nonprofitEducationImageUrl from 'bender-url!marketplace-assets-base/img/business-types/nonprofit-education.png';
import professionalServicesImageUrl from 'bender-url!marketplace-assets-base/img/business-types/professional-service.png';
import realEstateImageUrl from 'bender-url!marketplace-assets-base/img/business-types/real-estate.png';
import retailImageUrl from 'bender-url!marketplace-assets-base/img/business-types/retail.png';
import smallBusinessImageUrl from 'bender-url!marketplace-assets-base/img/business-types/small-business.png';
import technologyImageUrl from 'bender-url!marketplace-assets-base/img/business-types/technology.png';
import travelHospitalityImageUrl from 'bender-url!marketplace-assets-base/img/business-types/travel-hospitality.png';
import { getAssetBusinessTypeI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import * as ThemeQueryField from '../../constants/ThemeQueryField';
import { getBusinessTypes, getUsedBusinessTypes } from '../../selectors/themes';
import RouterFilterButton from './RouterFilterButton';
const ASSET_BUSINESS_TYPE_IMAGE_MAP = {
  [AssetBusinessType.ART_ENTERTAINMENT]: artEntertainmentImageUrl,
  [AssetBusinessType.CUSTOMER_SERVICE]: customerServiceImageUrl,
  [AssetBusinessType.ECOMMERCE]: ecommerceImageUrl,
  [AssetBusinessType.EVENT_MANAGEMENT]: eventManagementImageUrl,
  [AssetBusinessType.FINANCIAL_SERVICES]: financialServicesImageUrl,
  [AssetBusinessType.FOOD_BEVERAGE]: foodBeverageImageUrl,
  [AssetBusinessType.HEALTH_BEAUTY]: healthBeautyImageUrl,
  [AssetBusinessType.HR_RECRUITING]: hrRecruitingImageUrl,
  [AssetBusinessType.MARKETING]: marketingImageUrl,
  [AssetBusinessType.NONPROFIT_EDUCATION]: nonprofitEducationImageUrl,
  [AssetBusinessType.PROFESSIONAL_SERVICES]: professionalServicesImageUrl,
  [AssetBusinessType.REAL_ESTATE]: realEstateImageUrl,
  [AssetBusinessType.RETAIL]: retailImageUrl,
  [AssetBusinessType.SMALL_BUSINESS]: smallBusinessImageUrl,
  [AssetBusinessType.TECHNOLOGY]: technologyImageUrl,
  [AssetBusinessType.TRAVEL_HOSPITALITY]: travelHospitalityImageUrl
};
const StyledRouterFilterButton = styled(RouterFilterButton).withConfig({
  displayName: "ThemeBusinessTypesGrid__StyledRouterFilterButton",
  componentId: "ttx2my-0"
})(["&&:hover{text-decoration:none;}"]);
const AssetBusinessTypeButton = styled(props => {
  const {
    assetBusinessType,
    onClick
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["assetBusinessType", "onClick"]);

  return /*#__PURE__*/_jsx(StyledRouterFilterButton, {
    onClick: onClick,
    queryField: ThemeQueryField.BUSINESS_TYPES,
    value: assetBusinessType,
    use: "unstyled",
    children: /*#__PURE__*/_jsx("div", Object.assign({}, rest))
  });
}).withConfig({
  displayName: "ThemeBusinessTypesGrid__AssetBusinessTypeButton",
  componentId: "ttx2my-1"
})(["display:flex;align-items:center;justify-content:center;position:relative;margin-bottom:16px;padding:20px 48px;height:220px;width:100%;border-radius:", ";background:linear-gradient(rgba(51,71,91,0.6),rgba(51,71,91,0.6)),url(", ");background-position:center;background-size:100% 100%;background-repeat:no-repeat;transition:background-size 0.3s ease-out;text-align:center;color:", ";&:hover{background-size:105% 105%;}"], BUTTON_RADIUS, props => ASSET_BUSINESS_TYPE_IMAGE_MAP[props.assetBusinessType], OLAF);

const ThemeBusinessTypesGrid = ({
  limit,
  onTrackInteraction,
  showAll
}) => {
  const businessTypes = useSelector(getBusinessTypes);
  const totalBusinessTypes = businessTypes.length;
  const usedBusinessTypes = useSelector(getUsedBusinessTypes);
  const displayedBusinessTypes = useMemo(() => {
    const computedLimit = limit || totalBusinessTypes;
    return showAll ? businessTypes : usedBusinessTypes.slice(0, computedLimit);
  }, [businessTypes, limit, showAll, totalBusinessTypes, usedBusinessTypes]);
  const renderedGridItems = useMemo(() => {
    const sortedTuples = displayedBusinessTypes.map(assetBusinessType => {
      const assetBusinessTypeI18nKey = getAssetBusinessTypeI18nKey(assetBusinessType);
      return [assetBusinessType, I18n.text(assetBusinessTypeI18nKey)];
    }).sort(([__aAssetBusinessType, aAssetBusinessTypeString], [__bAssetBusinessType, bAssetBusinessTypeString]) => {
      return localeComparator(aAssetBusinessTypeString, bAssetBusinessTypeString);
    });
    return sortedTuples.map(([assetBusinessType, assetBusinessTypeString], index) => /*#__PURE__*/_jsx(UIGridItem, {
      role: "listitem",
      size: {
        xs: 12,
        md: 6,
        lg: 4
      },
      children: /*#__PURE__*/_jsx(AssetBusinessTypeButton, {
        assetBusinessType: assetBusinessType,
        onClick: () => {
          onTrackInteraction('click business type', {
            value: assetBusinessType,
            itemIndex: index,
            totalResults: displayedBusinessTypes.length
          });
        },
        children: /*#__PURE__*/_jsx("span", {
          className: "is--heading-1 m-bottom-0",
          children: assetBusinessTypeString
        })
      })
    }, `asset-business-type-${assetBusinessType}-item`));
  }, [displayedBusinessTypes, onTrackInteraction]);

  if (!renderedGridItems) {
    return null;
  }

  return /*#__PURE__*/_jsx(UIGrid, {
    role: "list",
    children: renderedGridItems
  });
};

ThemeBusinessTypesGrid.propTypes = {
  limit: PropTypes.number,
  onTrackInteraction: PropTypes.func,
  showAll: PropTypes.bool
};
ThemeBusinessTypesGrid.defaultProps = {
  onTrackInteraction: emptyFunction,
  showAll: false
};
export default ThemeBusinessTypesGrid;